/**
 * One shared Markdown behavior for the host notification row and the
 * rewards-admin inbox mock.
 *
 * Curated subset (not full CommonMark/GFM):
 * - Headings `#`–`######`
 * - Bold `**`, italic `*`, strikethrough `~~` (no `_` / `__` — those stay literal so snake_case ids are safe)
 * - Inline code `` `code` ``
 * - Links `[label](http(s)://…)` only
 * - Unordered / ordered lists
 * - Blockquotes `>`
 *
 * Raw HTML is escaped. Tables, images, task lists, and fenced code blocks
 * are not rendered as structure (markers may appear as text).
 */

/** Max visible body characters (markdown markers do not count). */
export const NOTIFICATION_BODY_VISIBLE_MAX = 350;

/**
 * CSS for injected notification HTML. Restores list markers that Tailwind /
 * global resets otherwise hide; sizes block elements for dense inbox rows.
 */
export const NOTIFICATION_MARKDOWN_CSS = `
.notification-md { word-break: break-word; overflow-wrap: anywhere; }
.notification-md p { margin: 0.25em 0; }
.notification-md p:first-child { margin-top: 0; }
.notification-md p:last-child { margin-bottom: 0; }
.notification-md p.notification-md-blank {
  margin: 0;
  min-height: 1em;
  line-height: 1em;
}
.notification-md h1, .notification-md h2, .notification-md h3,
.notification-md h4, .notification-md h5, .notification-md h6 {
  margin: 0.45em 0 0.25em;
  font-weight: 700;
  line-height: 1.2;
  color: inherit;
}
.notification-md h1 { font-size: 1.75em; }
.notification-md h2 { font-size: 1.45em; }
.notification-md h3 { font-size: 1.25em; }
.notification-md h4 { font-size: 1.1em; }
.notification-md h5, .notification-md h6 { font-size: 1em; font-weight: 600; }
.notification-md ul { list-style: disc; padding-left: 1.25rem; margin: 0.25em 0; }
.notification-md ol { list-style: decimal; padding-left: 1.25rem; margin: 0.25em 0; }
.notification-md li { margin: 0.125em 0; }
.notification-md a { color: inherit; text-decoration: underline; }
.notification-md strong { font-weight: 700; }
.notification-md em { font-style: italic; }
.notification-md del { text-decoration: line-through; opacity: 0.85; }
.notification-md code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.92em;
  padding: 0.05em 0.3em;
  border-radius: 3px;
  background: rgba(228, 228, 228, 0.12);
}
.notification-md blockquote {
  margin: 0.35em 0;
  padding: 0.15em 0 0.15em 0.65em;
  border-left: 2px solid rgba(228, 228, 228, 0.35);
  opacity: 0.92;
}
`;

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}

/** A safe href: absolute http(s) URL only. */
function safeHref(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.href;
  } catch {
    return null;
  }
  return null;
}

type Placeholder = { token: string; html: string };

function stash(placeholders: Placeholder[], html: string): string {
  const token = `\u0000MD${placeholders.length}\u0000`;
  placeholders.push({ token, html });
  return token;
}

function restore(text: string, placeholders: Placeholder[]): string {
  let out = text;
  for (let i = placeholders.length - 1; i >= 0; i -= 1) {
    out = out.split(placeholders[i].token).join(placeholders[i].html);
  }
  return out;
}

/** Inline formatting for a single line: code, links, strong, em, strike. */
function renderInline(rawText: string): string {
  const placeholders: Placeholder[] = [];
  let text = escapeHtml(rawText);

  // Inline code first so markers inside are not interpreted.
  text = text.replace(/`([^`]+)`/g, (_m, code: string) => stash(placeholders, `<code>${code}</code>`));

  // Links: [label](url)
  text = text.replace(/\[([^\]]*)\]\(([^)\s]+)\)/g, (match, label: string, url: string) => {
    const href = safeHref(url);
    if (!href) return match;
    return stash(
      placeholders,
      `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${label}</a>`,
    );
  });

  // Strong before em so `**` is not consumed by the single-asterisk rule.
  // Underscore emphasis is intentionally unsupported so identifiers like
  // `host_notifications_table` and `foo__bar__baz` stay literal.
  text = text.replace(/\*\*([^*]+)\*\*/g, (_m, inner: string) =>
    stash(placeholders, `<strong>${inner}</strong>`),
  );

  text = text.replace(/~~([^~]+)~~/g, (_m, inner: string) =>
    stash(placeholders, `<del>${inner}</del>`),
  );

  text = text.replace(/(^|[^*])\*([^*]+)\*/g, (_m, lead: string, inner: string) =>
    `${lead}${stash(placeholders, `<em>${inner}</em>`)}`,
  );

  return restore(text, placeholders);
}

/**
 * Renders the curated Markdown subset to HTML. Input is treated as untrusted:
 * raw HTML is escaped and unsafe link schemes stay text.
 */
export function renderNotificationMarkdown(markdown: string): string {
  if (!markdown) return '';

  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const output: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let quoteBuffer: string[] = [];

  const closeList = () => {
    if (listType) {
      output.push(`</${listType}>`);
      listType = null;
    }
  };

  const flushQuote = () => {
    if (quoteBuffer.length === 0) return;
    const inner = quoteBuffer.map((line) => `<p>${renderInline(line)}</p>`).join('');
    output.push(`<blockquote>${inner}</blockquote>`);
    quoteBuffer = [];
  };

  for (const line of lines) {
    const heading = line.match(/^\s*(#{1,6})\s+(.*)$/);
    const quote = line.match(/^\s*>\s?(.*)$/);
    const unordered = line.match(/^\s*[-*+]\s+(.*)$/);
    const ordered = line.match(/^\s*\d+\.\s+(.*)$/);

    if (heading) {
      flushQuote();
      closeList();
      const level = heading[1].length;
      output.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      continue;
    }

    if (quote) {
      closeList();
      quoteBuffer.push(quote[1]);
      continue;
    }

    flushQuote();

    if (unordered) {
      if (listType !== 'ul') {
        closeList();
        output.push('<ul>');
        listType = 'ul';
      }
      output.push(`<li>${renderInline(unordered[1])}</li>`);
      continue;
    }

    if (ordered) {
      if (listType !== 'ol') {
        closeList();
        output.push('<ol>');
        listType = 'ol';
      }
      output.push(`<li>${renderInline(ordered[1])}</li>`);
      continue;
    }

    closeList();
    if (line.trim() === '') {
      // Keep author blank lines as visible vertical space (not collapsed away).
      output.push('<p class="notification-md-blank">&nbsp;</p>');
      continue;
    }
    output.push(`<p>${renderInline(line)}</p>`);
  }

  flushQuote();
  closeList();
  return output.join('');
}

/** Strip curated Markdown markers; preserve newlines for the caller to handle. */
function stripNotificationMarkdownMarkers(markdown: string): string {
  let text = markdown.replace(/\r\n/g, '\n');

  // Links: keep the label, drop the URL.
  text = text.replace(/\[([^\]]*)\]\(([^)\s]+)\)/g, '$1');

  // Block markers.
  text = text.replace(/^\s*#{1,6}\s+/gm, '');
  text = text.replace(/^\s*>\s?/gm, '');
  text = text.replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, '');

  // Inline markers (asterisk / tilde / backtick only — underscores are literal).
  text = text.replace(/`([^`]+)`/g, '$1');
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/~~([^~]+)~~/g, '$1');
  text = text.replace(/\*([^*]+)\*/g, '$1');
  text = text.replace(/[`*~]/g, '');

  return text;
}

/** Turn rendered notification HTML into plain text (no tags, decoded entities). */
function htmlToPlainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|h[1-6]|li|blockquote|div|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/**
 * Collapsed-row preview: first non-empty line of visible words only.
 * Uses the same renderer as the expanded body so Markdown markers (`**`, `#`,
 * link URLs, list bullets, etc.) never appear as plain characters.
 * Does not pull later lines onto the glimpse — the row clips overflow in CSS.
 */
export function toNotificationGlimpse(markdown: string): string {
  if (!markdown) return '';

  const plain = htmlToPlainText(renderNotificationMarkdown(markdown));
  const firstLine = plain.split('\n').find((line) => line.trim() !== '') ?? '';
  return firstLine.replace(/\s+/g, ' ').trim();
}

/**
 * Visible character count for body limits (markdown syntax excluded).
 * Counts the full body, not just the collapsed glimpse line.
 */
export function countNotificationVisibleChars(markdown: string): number {
  if (!markdown) return 0;
  return stripNotificationMarkdownMarkers(markdown).replace(/\s+/g, ' ').trim().length;
}

/**
 * Truncates markdown so the visible character count is at most `max`.
 * Prefer cutting at a source index; may drop trailing incomplete markers.
 */
export function clampNotificationMarkdownToVisibleLimit(
  markdown: string,
  max: number = NOTIFICATION_BODY_VISIBLE_MAX,
): string {
  if (countNotificationVisibleChars(markdown) <= max) return markdown;

  let lo = 0;
  let hi = markdown.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (countNotificationVisibleChars(markdown.slice(0, mid)) <= max) lo = mid;
    else hi = mid - 1;
  }
  return markdown.slice(0, lo).trimEnd();
}
